import { useEffect, useLayoutEffect, useRef } from 'react';
import classnames from 'classnames';
import { gsap } from 'gsap';
import { isMobile } from 'react-device-detect';
import { useMedia } from 'utils/hooks';

import keyboardInstruction1 from 'images/instructions/use-keyboard-1.png';
import keyboardInstruction2 from 'images/instructions/use-keyboard-2.png';
import mouseInstruction from 'images/instructions/use-mouse.png';
import mobileInstruction1 from 'images/instructions/use-1.png';
import mobileInstruction2 from 'images/instructions/use-2.png';
import mobileInstruction3 from 'images/instructions/use-3.png';
import mobileInstruction4 from 'images/instructions/use-4.png';

import styles from './index.css';

export interface IntroProp {
    className?: string;
    backgroundImage: string;
    backgroundImageMobile?: string;
    logo?: string;
    logoClassName?: string;
    slogan: string;
    sloganClassName?: string;
    animeType?: 'smooth' | 'instant';
    loading?: boolean;
    onClickSkip?: () => void;
    onComplete?: () => void;
}

const Intro: React.FC<IntroProp> = ({
    className,
    backgroundImage,
    backgroundImageMobile,
    logo,
    logoClassName,
    slogan,
    sloganClassName,
    animeType = 'smooth',
    loading,
    onClickSkip = () => {},
    onComplete = () => {},
}) => {
    const mainTimeline = useRef<gsap.core.Timeline | null>(null);
    const leaveTimeline = useRef<gsap.core.Timeline | null>(null);
    const skipTimeline = useRef<gsap.core.Timeline | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);
    const bgImageRef = useRef<HTMLDivElement>(null);
    const brandRef = useRef<HTMLDivElement>(null);
    const instructionRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);
    const skipRef = useRef<HTMLButtonElement>(null);

    const { media } = useMedia();

    useLayoutEffect(() => {
        if (animeType === 'smooth') {
            mainTimeline.current = gsap
                .timeline({
                    delay: 0.2,
                })
                .timeScale(0.5)
                .fromTo(brandRef.current, { opacity: 0 }, { opacity: 1 })
                .timeScale(1)
                .to(brandRef.current, { opacity: 0 }, '+=1.2')
                .fromTo(instructionRef.current, { opacity: 0 }, { opacity: 1 });

            leaveTimeline.current = gsap
                .timeline({
                    paused: true,
                    onComplete,
                })
                .to(instructionRef.current, { opacity: 0, duration: 0.5 })
                .to(maskRef.current, { backgroundColor: 'rgba(0, 0, 0, 1)', duration: 0.5 }, '<')
                .to(bgImageRef.current, { opacity: 0, duration: 1 })
                .to(rootRef.current, { opacity: 0, duration: 1 });
        }

        skipTimeline.current = gsap
            .timeline({ paused: true })
            .timeScale(0.3)
            .fromTo(skipRef.current, { opacity: 1 }, { opacity: 0.3 })
            .yoyo(true)
            .repeat(-1);

        if (animeType === 'instant') {
            mainTimeline.current = gsap
                .timeline({})
                .fromTo(instructionRef.current, { opacity: 0 }, { opacity: 0 })
                .set(brandRef.current, { opacity: 1 })
                .set(brandRef.current, { opacity: 0 }, '+=0.5')
                .set(instructionRef.current, { opacity: 1 });

            leaveTimeline.current = gsap
                .timeline({
                    paused: true,
                    onComplete,
                })
                .set(bgImageRef.current, { opacity: 0 })
                .set(rootRef.current, { opacity: 0 });
        }

        return () => {
            mainTimeline.current?.kill();
            leaveTimeline.current?.kill();
            skipTimeline.current?.kill();
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            skipTimeline.current?.play();
        } else {
            skipTimeline.current?.pause();
        }
    }, [loading]);
    return (
        <div ref={rootRef} className={classnames(styles.intro, className)}>
            <div
                ref={bgImageRef}
                className={styles.bgImage}
                style={{
                    backgroundImage: `url(${
                        media === 'phone'
                            ? backgroundImageMobile || backgroundImage
                            : backgroundImage
                    })`,
                }}
            />
            <div ref={brandRef} className={classnames(styles.brand)}>
                {logo && (
                    <div className={classnames(styles.logo, logoClassName)}>
                        <img src={logo} alt="brand-logo" />
                    </div>
                )}
                <div className={classnames(styles.slogan, sloganClassName)}>{slogan}</div>
            </div>

            <div ref={instructionRef} className={styles.instruction}>
                {!isMobile && (
                    <div className={styles.contentDesktop}>
                        <div className={classnames(styles.group, styles.keyboard)}>
                            <div className={styles.imageWrapper}>
                                <div className={styles.image}>
                                    <img src={keyboardInstruction1} alt="keyboard-instruction-1" />
                                </div>
                                <div className={styles.image}>
                                    <img src={keyboardInstruction2} alt="keyboard-instruction-2" />
                                </div>
                            </div>
                            <div className={styles.text}>
                                With keyboard, use these keys above to navigate and look around.
                            </div>
                            <div className={styles.textZh}>請使用以上鍵盤按鍵進行走動 / 環顧</div>
                        </div>
                        <div className={classnames(styles.group, styles.mouse)}>
                            <div className={styles.imageWrapper}>
                                <div className={styles.image}>
                                    <img src={mouseInstruction} alt="mouse-instruction-1" />
                                </div>
                            </div>
                            <div className={styles.text}>
                                With mouse, click to move, drag to look around.
                            </div>
                            <div className={styles.textZh}>
                                請點擊滑鼠進行走動，拖曳滑鼠進行環顧
                            </div>
                        </div>
                    </div>
                )}
                {isMobile && (
                    <div className={styles.contentMobile}>
                        <div className={classnames(styles.group, styles.drag)}>
                            <div className={styles.image}>
                                <img src={mobileInstruction1} alt="mobile-instruction-1" />
                            </div>
                            <div className={styles.textWrapper}>
                                <div className={styles.text}>
                                    Touch and drag to look <br />
                                    around the space.
                                </div>
                                <div className={styles.textZh}>點擊並拖拉以環顧周圍空間</div>
                            </div>
                        </div>
                        <div className={classnames(styles.group, styles.zoom)}>
                            <div className={styles.image}>
                                <img src={mobileInstruction3} alt="mobile-instruction-3" />
                            </div>
                            <div className={styles.textWrapper}>
                                <div className={styles.text}>
                                    Use a pinch gesture to <br />
                                    zoom in and out.
                                </div>
                                <div className={styles.textZh}>使用兩指來進行放大和縮小</div>
                            </div>
                        </div>
                        <div className={classnames(styles.group, styles.tap)}>
                            <div className={styles.image}>
                                <img src={mobileInstruction2} alt="mobile-instruction-2" />
                            </div>
                            <div className={classnames(styles.textWrapper, styles.offsetTop)}>
                                <div className={classnames(styles.text, styles.offsetTop)}>
                                    Tap on the position spots or <br />
                                    direction on the screen throughout <br />
                                    the space to move around.
                                </div>
                                <div className={styles.textZh}>
                                    在螢幕上點擊位置或方向以進行走動
                                </div>
                            </div>
                        </div>
                        <div className={classnames(styles.group, styles.information)}>
                            <div className={styles.image}>
                                <img src={mobileInstruction4} alt="mobile-instruction-4" />
                            </div>
                            <div className={classnames(styles.textWrapper)}>
                                <div className={styles.text}>
                                    Click on the info icon to get <br />
                                    informations / to play video.
                                </div>
                                <div className={styles.textZh}>
                                    點擊資訊點以檢視詳細資訊 / 播放影片
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <button
                    ref={skipRef}
                    className={classnames(
                        styles.skip,
                        isMobile && styles.mobile,
                        loading && styles.loading,
                    )}
                    type="button"
                    onClick={() => {
                        if (!loading) {
                            onClickSkip();
                            leaveTimeline.current?.play();
                        }
                    }}
                >
                    {loading ? 'loading ...' : 'Start'}
                </button>
            </div>
        </div>
    );
};

export default Intro;
